import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus, FaTimes, FaTrash, FaEdit, FaFileInvoiceDollar,
  FaChevronDown, FaChevronUp, FaSearch, FaExchangeAlt, FaFilePdf, FaBan
} from 'react-icons/fa';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import InputProductoFacturacion from '../util/Inputs/InputProductoFacturacion';
import InputClientes from '../util/Inputs/InputClientes';
import TablaEntidad from '../util/tablas/TablaEntidad';
import PlantillaModal from '../util/PlantillaModal';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import FuncionesGenerales from '../util/FuncionesGenerales';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalCrearClienteRapido from './ModalCrearClienteRapido';
import ModalCrearProductoRapido from './ModalCrearProductoRapido';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import ModalConfirmacion from '../util/ModalConfirmacion';
import useTicketManager from '../../hooks/useTicketManager';
import { roundTo2 } from '../../miscelaneo/monetario';
import { noEsFechaHoraFutura } from '../util/ValidacionesCampos';
import { useImpresion } from '../../contexts/ContextoImpresion';
import { useBalanza } from '../../contexts/ContextoBalanza';
import { useScanner } from '../../contexts/ContextoScanner';

export default function ProcesoCotizacion() {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, cargando: cargandoContexto, tienePermiso } = useContextoSesion();
  const { formatoMoneda, obtenerFechaHoraActual, formatearFechaHoraInput } = FuncionesGenerales(usuario);

  // El hook se reutiliza sin cambios. tipo_comprobante = null (no aplica para cotizaciones).
  const tm = useTicketManager({ defaultTipoComprobante: null });
  const { imprimirDocumento } = useImpresion();
  const { pesoActual, unidadPeso, balanzaEstable, balanzaConectada, pesoBloqueado, bloquearPeso, desbloquearPeso, sinDatos } = useBalanza();
  const { scannerConectado, habilitado: scannerHidHabilitado, registrarListener } = useScanner();

  const [mostrarModalNuevoProducto, setMostrarModalNuevoProducto] = useState(false);

  // Estado búsqueda de cotizaciones editables
  const [mostrarModalBuscarEditable, setMostrarModalBuscarEditable] = useState(false);
  const [cotizacionesEditables, setCotizacionesEditables] = useState([]);
  const [cargandoEditables, setCargandoEditables] = useState(false);
  const [filtroEditable, setFiltroEditable] = useState('');

  // Modal de conversión a factura
  const [mostrarModalConvertir, setMostrarModalConvertir] = useState(false);
  const [convertirDestino, setConvertirDestino] = useState('factura_demo');
  const [convirtiendo, setConvirtiendo] = useState(false);

  // Modal de PDF de cotización
  const [mostrarPDFCotizacion, setMostrarPDFCotizacion] = useState(false);
  const [idCotizacionPDF, setIdCotizacionPDF] = useState(null);

  // Estado de cotización actualmente cargada (en modo edición)
  const [cotizacionCargada, setCotizacionCargada] = useState(null); // { id_cotizacion, id_factura_convertida }

  // Cargar cotización por editId cuando se navega desde PantallaCotizaciones
  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId || tm.cargando || cargandoContexto) return;
    // Limpiar state para evitar re-carga al hacer hot-reload
    window.history.replaceState({}, document.title);
    cargarCotizacionParaEditar({ id_cotizacion: editId });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editId, tm.cargando, cargandoContexto]);

  // Focus automático en input de producto
  useEffect(() => {
    if (!tm.cargando && !cargandoContexto && tm.sucursalSeleccionada && tm.inputProductoRef.current) {
      setTimeout(() => { tm.inputProductoRef.current?.focus?.(); }, 200);
    }
  }, [tm.cargando, cargandoContexto, tm.sucursalSeleccionada]);

  // Scanner HID
  useEffect(() => {
    if (!scannerHidHabilitado || !scannerConectado) return;
    const unsubscribe = registrarListener((barcode) => {
      tm.inputProductoRef.current?.obtenerProducto(barcode, { rapido: true });
    });
    return unsubscribe;
  }, [scannerHidHabilitado, scannerConectado, registrarListener, tm.inputProductoRef]);

  // ============================================================================
  // BUSCAR Y CARGAR COTIZACIÓN PARA EDITAR
  // ============================================================================

  const buscarCotizacionesEditables = async (busqueda) => {
    try {
      setCargandoEditables(true);
      const params = { limite: 50, id_estado: 1 };
      if (busqueda) params.busqueda = busqueda;
      const response = await instanciaAxios.get('/cotizacion', { params });
      const lista = response.data?.cotizaciones || response.data || [];
      // Solo las que no han sido convertidas
      setCotizacionesEditables(lista.filter(c => !c.id_factura_convertida));
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al buscar cotizaciones');
    } finally {
      setCargandoEditables(false);
    }
  };

  const cargarCotizacionParaEditar = async (cotizacion) => {
    try {
      const response = await instanciaAxios.get(`/cotizacion/${cotizacion.id_cotizacion}`);
      const cot = response.data;

      const productos = (cot.detalles_cotizacion || []).map(det => ({
        id_producto:           det.id_producto,
        descripcion:           det.descripcion_producto,
        precio:                parseFloat(det.precio) || 0,
        precioOriginal:        parseFloat(det.precio) || 0,
        precioBase:            parseFloat(det.precio) || 0,
        precioLista:           null,
        fuentePrecio:          'base',
        precioEditado:         false,
        cantidad:              parseFloat(det.cantidad) || 1,
        indicador_facturacion: det.indicador_facturacion || 1,
        itbis_tasa:            parseFloat(det.itbis_tasa) || 0,
        id_tipo_producto:      det.id_tipo_producto || 1
      }));

      const nuevosTickets = [...tm.tickets];
      const ticket = nuevosTickets[tm.ticketActivo];
      ticket.productos             = productos;
      ticket.clienteSeleccionado   = cot.id_entidad_cliente || null;
      ticket.id_entidad_cliente_original = cot.id_entidad_cliente || null;
      ticket.id_lista_precio       = cot.id_lista_precio || null;
      ticket.nombreCliente         = cot.nombre_cliente || '';
      ticket.comentario            = cot.comentario || '';
      ticket.fecha_hora_creacion   = formatearFechaHoraInput(cot.fecha_hora_creacion || obtenerFechaHoraActual());
      ticket.id_factura_editando   = cot.id_cotizacion; // reusamos este campo del hook
      ticket.productosOriginales   = productos.map(p => ({ ...p }));
      ticket.tipoComprobanteFiscal = null;

      tm.calcularTotales(ticket);
      tm.setTickets(nuevosTickets);

      setCotizacionCargada({
        id_cotizacion:         cot.id_cotizacion,
        id_factura_convertida: cot.id_factura_convertida
      });

      setMostrarModalBuscarEditable(false);
      toast.success(`Cotización #${cot.id_cotizacion} cargada para edición`);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar la cotización para edición');
    }
  };

  // ============================================================================
  // GUARDAR COTIZACIÓN
  // ============================================================================

  const actualizarComentarioTicket = (comentario) => {
    const nuevosTickets = [...tm.tickets];
    nuevosTickets[tm.ticketActivo].comentario = comentario;
    tm.setTickets(nuevosTickets);
  };

  const normalizarComentarioTicket = () => {
    const c = tm.tickets[tm.ticketActivo]?.comentario;
    if (typeof c !== 'string') return;
    const normalizado = c.trim();
    if (normalizado === c) return;
    actualizarComentarioTicket(normalizado);
  };

  const guardarCotizacion = async () => {
    const ticket = tm.ticketActual;

    if (ticket.productos.length === 0) { toast.error('La cotización está vacía'); return; }
    if (!tm.sucursalSeleccionada)      { toast.error('Debe seleccionar una sucursal'); return; }
    if (tm.procesandoCobro) return;

    const fechaHoraCreacion = ticket.fecha_hora_creacion || obtenerFechaHoraActual();
    if (!noEsFechaHoraFutura(fechaHoraCreacion)) {
      toast.error('La fecha y hora no puede ser futura');
      return;
    }

    try {
      tm.setProcesandoCobro(true);

      const detallesCotizacion = ticket.productos.map(p => ({
        id_producto:           p.id_producto,
        descripcion_producto:  p.descripcion,
        cantidad:              p.cantidad,
        precio:                p.precio,
        descuento:             0,
        indicador_facturacion: p.indicador_facturacion,
        itbis_tasa:            p.itbis_tasa,
        id_tipo_producto:      p.id_tipo_producto || 1
      }));

      const payload = {
        id_entidad_cliente: ticket.clienteSeleccionado ?? null,
        id_sucursal:        tm.sucursalSeleccionada,
        id_moneda:          1,
        id_lista_precio:    ticket.id_lista_precio ?? null,
        fecha_hora_creacion: fechaHoraCreacion,
        comentario:         typeof ticket.comentario === 'string' ? ticket.comentario.trim() || null : null,
        detallesCotizacion,
        monto:         roundTo2(ticket.totales.subtotal),
        impuesto_total: roundTo2(ticket.totales.itbis),
        monto_total:    roundTo2(ticket.totales.total)
      };

      const enEdicion = !!ticket.id_factura_editando;

      let respuesta;
      if (enEdicion) {
        respuesta = await instanciaAxios.put(
          `/cotizacion/${ticket.id_factura_editando}/completa`,
          payload
        );
        toast.success(`Cotización #${ticket.id_factura_editando} actualizada`);
        setCotizacionCargada(prev => ({ ...prev, id_cotizacion: ticket.id_factura_editando }));
      } else {
        respuesta = await instanciaAxios.post('/cotizacion/', payload);
        const { id_cotizacion } = respuesta.data;
        toast.success(`Cotización #${id_cotizacion} guardada exitosamente`);
        setCotizacionCargada({ id_cotizacion, id_factura_convertida: null });

        // Imprimir PDF si está disponible
        await imprimirDocumento({
          idDocumento:   id_cotizacion,
          tipoDocumento: 'cotizacion',
          pdfBase64:     null
        });

        tm.limpiarTicketActivo();
        setCotizacionCargada(null);
      }

    } catch (error) {
      if (esErrorPermiso(error)) return;
      const mensaje = error.response?.data?.mensaje || error.response?.data?.message || 'Error al guardar la cotización';
      if (error.response?.status === 400) {
        toast.error(mensaje);
        return;
      }
      toast.error(mensaje);
    } finally {
      tm.setProcesandoCobro(false);
    }
  };

  // ============================================================================
  // ANULAR COTIZACIÓN
  // ============================================================================

  const anularCotizacion = async () => {
    const id = cotizacionCargada?.id_cotizacion;
    if (!id) return;
    try {
      await instanciaAxios.put(`/cotizacion/${id}/anular`);
      toast.success(`Cotización #${id} anulada`);
      tm.limpiarTicketActivo();
      setCotizacionCargada(null);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      const mensaje = error.response?.data?.mensaje || 'Error al anular la cotización';
      toast.error(mensaje);
    }
  };

  // ============================================================================
  // CONVERTIR A FACTURA
  // ============================================================================

  const confirmarConversion = async () => {
    const id = cotizacionCargada?.id_cotizacion ?? tm.ticketActual.id_factura_editando;
    if (!id) return;
    try {
      setConvirtiendo(true);
      const { data } = await instanciaAxios.get(`/cotizacion/${id}`);
      setMostrarModalConvertir(false);
      tm.limpiarTicketActivo();
      setCotizacionCargada(null);
      // Navegar al destino elegido con los datos de la cotización precargados
      // (la factura solo se crea cuando el usuario presiona Cobrar en esa pantalla)
      const ruta = convertirDestino === 'pos' ? '/ProcesoPOS' : '/FacturaDemo';
      navigate(ruta, { state: { cotizacionOrigen: data } });
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar la cotización para conversión');
    } finally {
      setConvirtiendo(false);
    }
  };

  // ============================================================================
  // PDF
  // ============================================================================

  const verPDFCotizacion = async (idCotizacion) => {
    try {
      setIdCotizacionPDF(idCotizacion);
      const metodo = await imprimirDocumento({ tipoDocumento: 'cotizacion', idDocumento: idCotizacion });
      if (!metodo) setMostrarPDFCotizacion(true);
    } catch (error) {
      setMostrarPDFCotizacion(true);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (tm.cargando || cargandoContexto) {
    return <SpinnerCargando />;
  }

  const ticketActual = tm.ticketActual;
  const enModoEdicion = !!ticketActual.id_factura_editando;
  const estaConvertida = cotizacionCargada?.id_factura_convertida;

  return (
    <div className="min-h-screen bg-gray-200 font-sans">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border p-8 w-full max-w-7xl mx-10">

          {/* Header */}
          <div className="space-y-1 mb-6">
            <h1 className="font-cascadia font-bold text-2xl flex items-center gap-2">
              <FaFileInvoiceDollar className="text-color-secundario" />
              Cotización
            </h1>
            <p className="text-sm text-gray-500">Documento sin validez fiscal</p>
          </div>

          {/* Banner convertida (solo lectura) */}
          {estaConvertida && (
            <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-xl flex items-center gap-3">
              <FaExchangeAlt className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800">
                  Cotización convertida — Factura #{cotizacionCargada.id_factura_convertida}
                </p>
                <p className="text-sm text-green-700">
                  Esta cotización ya fue convertida a factura y está en modo solo lectura.
                </p>
              </div>
            </div>
          )}

          {/* Banner modo edición */}
          {enModoEdicion && !estaConvertida && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-center gap-3">
              <FaEdit className="text-amber-600 text-xl flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">
                  Modo Edición — Cotización #{ticketActual.id_factura_editando}
                </p>
                <p className="text-sm text-amber-700">
                  Los cambios se guardarán en la cotización existente.
                </p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('¿Desea cancelar la edición? Los cambios no guardados se perderán.')) {
                    tm.limpiarTicketActivo();
                    setCotizacionCargada(null);
                  }
                }}
                className="ml-auto px-3 py-1 bg-amber-200 text-amber-800 rounded-lg hover:bg-amber-300 transition text-sm font-medium"
              >
                Cancelar edición
              </button>
            </div>
          )}

          {/* Selector de Sucursal */}
          <div className="mb-6">
            {!tm.sucursalSeleccionada ? (
              <div className="bg-white p-4 rounded-xl border border-gray-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sucursal <span className="text-red-500">*</span>
                </label>
                <select
                  value={tm.sucursalSeleccionada}
                  onChange={(e) => tm.setSucursalSeleccionada(e.target.value)}
                  className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                >
                  <option value="">Selecciona una sucursal</option>
                  {tm.sucursales.map((s) => (
                    <option key={s.id_sucursal} value={s.id_sucursal}>{s.descripcion}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex justify-start items-center">
                <div className="bg-color-primario text-white px-4 py-2 rounded-lg flex items-center gap-3">
                  <span className="font-semibold">
                    Sucursal: {tm.sucursales.find(s => s.id_sucursal === parseInt(tm.sucursalSeleccionada))?.descripcion}
                  </span>
                  {!tm.sucursalDefecto && (
                    <button
                      onClick={() => {
                        if (tm.tickets.some(t => t.productos.length > 0)) {
                          if (!window.confirm('Hay tickets con productos. ¿Seguro que desea cambiar de sucursal?')) return;
                        }
                        tm.setSucursalSeleccionada('');
                      }}
                      className="text-xs bg-white text-color-primario px-2 py-1 rounded hover:bg-gray-100 transition"
                    >
                      Cambiar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
              {tm.tickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap ${
                    tm.ticketActivo === index
                      ? 'bg-color-secundario text-white border-color-secundario'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => tm.setTicketActivo(index)}
                >
                  <span className="font-medium text-sm">{ticket.nombre}</span>
                  {ticket.productos.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      tm.ticketActivo === index ? 'bg-white text-color-secundario' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {ticket.productos.length}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); tm.cerrarTicket(index); }}
                    className="hover:text-red-500 transition-colors"
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              ))}
              {tm.tickets.length < 10 && (
                <button
                  onClick={tm.crearNuevoTicket}
                  className="flex items-center gap-1 px-3 py-2 bg-color-primario text-white rounded-t-lg hover:bg-opacity-90 transition-colors flex-shrink-0 text-sm"
                >
                  <FaPlus size={10} />
                  Nuevo
                </button>
              )}
            </div>
          </div>

          {/* Info del ticket activo */}
          <div className="mb-6 bg-white p-4 rounded-xl border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {enModoEdicion
                ? `Editando Cotización #${ticketActual.id_factura_editando}`
                : `Información de ${ticketActual.nombre}`}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente <span className="text-gray-400">(opcional)</span>
                </label>
                <InputClientes
                  valor={ticketActual.clienteSeleccionado}
                  onClick={tm.handleClickBuscarCliente}
                  nombreCliente={ticketActual.nombreCliente}
                  placeholder="Sin cliente seleccionado"
                  editable={!estaConvertida}
                />
                {!estaConvertida && (
                  <button
                    onClick={() => tm.setMostrarModalNuevoCliente(true)}
                    className="text-sm text-color-primario hover:text-color-secundario mt-1 underline"
                  >
                    ¿Es un nuevo cliente? Crear aquí
                  </button>
                )}
              </div>

              {/* Tipo documento — informativo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Documento</label>
                <div className="w-full p-2 rounded-xl text-gray-500 border-2 border-gray-200 bg-gray-50">
                  Sin comprobante fiscal
                </div>
              </div>

              {/* Comentario */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Comentario</label>
                <textarea
                  value={ticketActual.comentario || ''}
                  onChange={(e) => actualizarComentarioTicket(e.target.value)}
                  onBlur={normalizarComentarioTicket}
                  disabled={!!estaConvertida}
                  className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-400"
                  placeholder="Comentario opcional (se imprime en el PDF)"
                  rows={2}
                />
              </div>
            </div>

            {/* Campos adicionales */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => tm.setMostrarCamposAdicionales(!tm.mostrarCamposAdicionales)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-color-primario transition-colors"
              >
                {tm.mostrarCamposAdicionales ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                <span>Campos adicionales</span>
              </button>

              {tm.mostrarCamposAdicionales && (
                <div className="mt-4 grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha y hora de creación
                    </label>
                    <input
                      type="datetime-local"
                      value={ticketActual.fecha_hora_creacion || ''}
                      max={obtenerFechaHoraActual()}
                      disabled={!!estaConvertida}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].fecha_hora_creacion = e.target.value;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Layout principal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">

              {/* Búsqueda de producto */}
              {!estaConvertida && (
                <div className="bg-white p-6 rounded-xl border border-gray-300">
                  <label className="block text-lg font-semibold text-gray-800 mb-3">Buscar Producto</label>
                  <p className="text-sm text-gray-500 mb-3">Escanea código de barras o busca por nombre/referencia</p>
                  <InputProductoFacturacion
                    ref={tm.inputProductoRef}
                    onProductoSeleccionado={tm.handleProductoSeleccionado}
                    onProductoRapido={tm.agregarProductoRapido}
                    onClickConProducto={tm.handleClickInputConProducto}
                    id_sucursal={tm.sucursalSeleccionada}
                    id_lista_precio={ticketActual.id_lista_precio}
                    modoFacturacion="venta"
                    editable={!!tm.sucursalSeleccionada}
                    placeholder="Referencia, código de barras o ID"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <button
                      onClick={() => setMostrarModalNuevoProducto(true)}
                      className="text-sm text-color-primario hover:text-color-secundario underline"
                    >
                      ¿Producto no existe? Crear aquí
                    </button>
                    {balanzaConectada && (
                      <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${sinDatos ? 'bg-red-100 text-red-700' : balanzaEstable ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sinDatos ? 'bg-red-500 animate-pulse' : balanzaEstable ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                        {sinDatos ? 'Sin datos' : `${pesoActual.toFixed(3)} ${unidadPeso}`}
                      </span>
                    )}
                    {scannerHidHabilitado && scannerConectado && (
                      <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Scanner HID
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Lista de productos */}
              <div className="bg-white p-6 rounded-xl border border-gray-300">
                <h2 className="text-lg font-semibold mb-4">
                  {enModoEdicion ? 'Productos de la Cotización' : 'Productos en Cotización'}
                </h2>

                {ticketActual.productos.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FaFileInvoiceDollar size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Ticket vacío</p>
                    <p className="text-sm">Agrega productos para comenzar</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-color-primario text-white">
                        <tr>
                          <th className="px-4 py-3 text-left">Producto</th>
                          <th className="px-4 py-3 text-right">Precio</th>
                          <th className="px-4 py-3 text-center">Cant.</th>
                          <th className="px-4 py-3 text-right">Subtotal</th>
                          {!estaConvertida && <th className="px-4 py-3 text-center">Acciones</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ticketActual.productos.map((producto, index) => (
                          <tr
                            key={index}
                            className="border-b hover:bg-gray-50 cursor-pointer"
                            onClick={() => !estaConvertida && tm.editarCantidadProducto(index)}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium">{producto.descripcion}</div>
                              <div className="text-xs text-gray-500">
                                {producto.indicador_facturacion === 4 ? 'Exento' : `ITBIS ${producto.itbis_tasa}%`}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span>{formatoMoneda(producto.precio)}</span>
                              {producto.precioEditado && (
                                <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">Precio mod.</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold">{producto.cantidad}</td>
                            <td className="px-4 py-3 text-right font-semibold text-color-secundario">
                              {formatoMoneda(producto.precio * producto.cantidad + tm.calcularImpuestoPorProducto(producto))}
                            </td>
                            {!estaConvertida && (
                              <td className="px-4 py-3">
                                <div className="flex justify-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); tm.editarCantidadProducto(index); }}
                                    className="text-color-primario hover:text-color-secundario transition"
                                    title="Editar cantidad"
                                  >
                                    <FaEdit size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); tm.eliminarProducto(index); }}
                                    className="text-red-500 hover:text-red-700 transition"
                                    title="Eliminar"
                                  >
                                    <FaTrash size={16} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Panel derecho — totales y acciones */}
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl border border-gray-300 sticky top-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">Resumen</h2>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ITBIS</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.itbis)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-color-secundario text-xl">{formatoMoneda(ticketActual.totales.total)}</span>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {/* Guardar cotización */}
                  {!estaConvertida && (
                    <button
                      onClick={guardarCotizacion}
                      disabled={tm.procesandoCobro || ticketActual.productos.length === 0 || !tm.sucursalSeleccionada}
                      className="w-full py-3 rounded-xl font-semibold text-white bg-color-primario hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {tm.procesandoCobro
                        ? 'Guardando...'
                        : enModoEdicion ? 'Actualizar Cotización' : 'Guardar Cotización'}
                    </button>
                  )}

                  {/* Convertir a factura */}
                  {enModoEdicion && !estaConvertida && tienePermiso('cotizacion_convertir') && (
                    <button
                      onClick={() => setMostrarModalConvertir(true)}
                      className="w-full py-3 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                    >
                      <FaExchangeAlt />
                      Convertir a Factura
                    </button>
                  )}

                  {/* PDF de cotización guardada */}
                  {enModoEdicion && cotizacionCargada?.id_cotizacion && (
                    <button
                      onClick={() => verPDFCotizacion(cotizacionCargada.id_cotizacion)}
                      className="w-full py-2 rounded-xl font-medium text-color-primario border-2 border-color-primario hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                    >
                      <FaFilePdf />
                      Ver PDF
                    </button>
                  )}

                  {/* Anular cotización */}
                  {enModoEdicion && !estaConvertida && tienePermiso('cotizacion_anular') && (
                    <button
                      onClick={() => {
                        if (window.confirm(`¿Está seguro de anular la cotización #${cotizacionCargada?.id_cotizacion}?`)) {
                          anularCotizacion();
                        }
                      }}
                      className="w-full py-2 rounded-xl font-medium text-red-600 border-2 border-red-300 hover:bg-red-50 transition-all flex items-center justify-center gap-2"
                    >
                      <FaBan />
                      Anular Cotización
                    </button>
                  )}

                  {/* Editar cotización existente */}
                  {!enModoEdicion && (
                    <button
                      onClick={() => {
                        setMostrarModalBuscarEditable(true);
                        buscarCotizacionesEditables('');
                      }}
                      className="w-full mt-1 py-2 rounded-xl font-medium text-sm transition-all border-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                    >
                      <FaSearch className="inline mr-2" />
                      Editar cotización existente
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MODALES ===== */}

      {/* Modal cantidad / editar producto */}
      <PlantillaModal
        mostrar={tm.mostrarModalCantidad}
        onClose={() => {
          tm.setMostrarModalCantidad(false);
          tm.setProductoTemporal(null);
          tm.setCantidadTemporal('1');
          tm.setPrecioTemporal('');
          tm.setModoModal('agregar');
          tm.setIndexProductoEditar(null);
          tm.setModoPrecioTemporal('unitario');
          desbloquearPeso();
          setTimeout(() => { tm.inputProductoRef.current?.limpiar?.(); }, 100);
        }}
        titulo={tm.modoModal === 'editar' ? 'Editar Producto' : 'Agregar Producto'}
      >
        {tm.productoTemporal && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Producto:</p>
              <p className="font-semibold text-lg">{tm.productoTemporal.descripcion}</p>
              {tm.productoTemporal.descripcion_unidad_medida && (
                <p className="text-sm text-gray-500 italic mt-1">Unidad: {tm.productoTemporal.descripcion_unidad_medida}</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Precio unitario:</label>
                {tienePermiso('cotizacion_editar_precio') && (
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => tm.setModoPrecioTemporal('unitario')}
                      className={`px-2 py-1 transition-colors ${tm.modoPrecioTemporal === 'unitario' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Sin impuestos
                    </button>
                    <button
                      type="button"
                      onClick={() => tm.setModoPrecioTemporal('total')}
                      className={`px-2 py-1 transition-colors border-l border-gray-300 ${tm.modoPrecioTemporal === 'total' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Con impuestos
                    </button>
                  </div>
                )}
              </div>

              {tienePermiso('cotizacion_editar_precio') ? (
                tm.modoPrecioTemporal === 'total' ? (
                  <>
                    <input
                      type="number" step="0.01"
                      value={tm.totalTemporalConImpuesto}
                      onChange={(e) => tm.actualizarTotalTemporal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') tm.inputCantidadRef.current?.focus(); }}
                      placeholder="Total con ITBIS incluido"
                      className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                      tabIndex={1}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Precio unitario (base): {formatoMoneda(parseFloat(tm.precioTemporal) || 0)}
                    </p>
                  </>
                ) : (
                  <input
                    type="number" step="0.01"
                    value={tm.precioTemporal}
                    onChange={(e) => tm.setPrecioTemporal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') tm.inputCantidadRef.current?.focus(); }}
                    className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                    tabIndex={1}
                  />
                )
              ) : (
                <p className="font-semibold text-xl text-color-secundario">
                  {formatoMoneda(tm.productoTemporal.precio || 0)}
                </p>
              )}

              <p className="text-xs text-gray-400 mt-1">
                Tasa ITBIS: {tm.obtenerTasaItbisEfectiva(tm.productoTemporal)}% (no editable)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad{tm.productoTemporal.descripcion_unidad_medida && ` (${tm.productoTemporal.descripcion_unidad_medida})`}:
              </label>
              <input
                ref={tm.inputCantidadRef}
                type="number"
                value={tm.cantidadTemporal}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) tm.setCantidadTemporal(v);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') tm.agregarProductoAlTicket(); }}
                min="0.01" step="0.01"
                className="w-full p-3 text-2xl text-center font-bold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                autoFocus tabIndex={2}
              />
            </div>
            {balanzaConectada && (
              <button
                onClick={() => {
                  if (balanzaEstable && !sinDatos) {
                    tm.setCantidadTemporal(pesoActual.toFixed(3));
                    bloquearPeso();
                  }
                }}
                className={`w-full py-2 text-sm rounded-lg border transition ${balanzaEstable && !sinDatos ? 'border-green-400 text-green-700 hover:bg-green-50' : 'border-gray-300 text-gray-400 cursor-not-allowed'}`}
                disabled={!balanzaEstable || sinDatos}
              >
                Usar peso: {sinDatos ? '---' : `${pesoActual.toFixed(3)} ${unidadPeso}`}
                {pesoBloqueado && ' 🔒'}
              </button>
            )}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>Antes de imp:</span>
                <span>
                  {formatoMoneda((parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0))}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-600">Con imp:</span>
                <span className="text-2xl font-bold text-color-primario">
                  {formatoMoneda(
                    (parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0) *
                    (1 + tm.obtenerTasaItbisEfectiva(tm.productoTemporal) / 100)
                  )}
                </span>
              </div>
            </div>
            <button
              onClick={tm.agregarProductoAlTicket}
              className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all"
              tabIndex={3}
            >
              {tm.modoModal === 'editar' ? 'Actualizar' : 'Agregar al Ticket'}
            </button>
          </div>
        )}
      </PlantillaModal>

      {/* Modal buscar clientes */}
      <PlantillaModal
        mostrar={tm.mostrarModalClientes}
        onClose={() => tm.setMostrarModalClientes(false)}
        titulo="Buscar Cliente"
      >
        <TablaEntidad
          tipoEntidad={1}
          descripcion="Seleccione un cliente"
          setEntidadSeleccionada={tm.handleSeleccionarCliente}
        />
      </PlantillaModal>

      {/* Modal crear cliente rápido */}
      <ModalCrearClienteRapido
        mostrar={tm.mostrarModalNuevoCliente}
        onClose={() => tm.setMostrarModalNuevoCliente(false)}
        onClienteCreado={tm.handleClienteCreado}
        tiposComprobantesFiscales={tm.tiposComprobantesFiscales}
      />

      {/* Modal crear producto rápido */}
      <ModalCrearProductoRapido
        mostrar={mostrarModalNuevoProducto}
        onClose={() => setMostrarModalNuevoProducto(false)}
        onProductoCreado={(producto) => {
          setMostrarModalNuevoProducto(false);
          if (producto && tm.inputProductoRef.current) {
            tm.inputProductoRef.current?.obtenerProducto(producto.id_producto);
          }
        }}
      />

      {/* Modal buscar cotización para editar */}
      <PlantillaModal
        mostrar={mostrarModalBuscarEditable}
        onClose={() => setMostrarModalBuscarEditable(false)}
        titulo="Buscar Cotización"
      >
        <div className="space-y-4">
          <input
            type="text"
            value={filtroEditable}
            onChange={(e) => {
              setFiltroEditable(e.target.value);
              buscarCotizacionesEditables(e.target.value);
            }}
            placeholder="Buscar por número o cliente..."
            className="w-full p-2 border-2 border-gray-300 rounded-xl outline-none focus:border-blue-500"
            autoFocus
          />
          {cargandoEditables ? (
            <p className="text-center text-gray-500 py-4">Buscando...</p>
          ) : cotizacionesEditables.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No hay cotizaciones activas sin convertir</p>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-2">
              {cotizacionesEditables.map((cot) => (
                <button
                  key={cot.id_cotizacion}
                  onClick={() => cargarCotizacionParaEditar(cot)}
                  className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-color-primario hover:bg-blue-50 transition"
                >
                  <p className="font-semibold text-sm">Cotización #{cot.id_cotizacion}</p>
                  <p className="text-xs text-gray-500">{cot.nombre_cliente || 'Sin cliente'} — {new Date(cot.fecha_hora_creacion).toLocaleDateString()}</p>
                  <p className="text-xs font-medium text-color-secundario">{formatoMoneda(cot.monto_total)}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </PlantillaModal>

      {/* Modal convertir a factura */}
      <PlantillaModal
        mostrar={mostrarModalConvertir}
        onClose={() => !convirtiendo && setMostrarModalConvertir(false)}
        titulo="Convertir a Factura"
      >
        <div className="space-y-5">
          <p className="text-gray-600 text-sm">
            La cotización #{cotizacionCargada?.id_cotizacion || ticketActual.id_factura_editando} se convertirá en una factura.
            <br />Selecciona hacia dónde deseas ir para procesar el cobro:
          </p>

          <div className="space-y-3">
            <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${convertirDestino === 'factura_demo' ? 'border-color-primario bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio" name="destino" value="factura_demo"
                checked={convertirDestino === 'factura_demo'}
                onChange={() => setConvertirDestino('factura_demo')}
              />
              <div>
                <p className="font-semibold text-sm">Factura Demo</p>
                <p className="text-xs text-gray-500">Para facturas sin comprobante fiscal o con e-NCF</p>
              </div>
            </label>

            <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${convertirDestino === 'pos' ? 'border-color-primario bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio" name="destino" value="pos"
                checked={convertirDestino === 'pos'}
                onChange={() => setConvertirDestino('pos')}
              />
              <div>
                <p className="font-semibold text-sm">Punto de Venta (POS)</p>
                <p className="text-xs text-gray-500">Para cobrar en caja con sesión activa</p>
              </div>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setMostrarModalConvertir(false)}
              disabled={convirtiendo}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarConversion}
              disabled={convirtiendo}
              className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <FaExchangeAlt />
              {convirtiendo ? 'Convirtiendo...' : 'Convertir →'}
            </button>
          </div>
        </div>
      </PlantillaModal>

      {/* PDF de cotización */}
      <ModalPDFBase64
        mostrar={mostrarPDFCotizacion}
        onClose={() => setMostrarPDFCotizacion(false)}
        idCotizacion={idCotizacionPDF}
        tituloDocumento="Cotización"
        tipoDocumento="cotizacion"
      />

      {/* Confirmación cambio de lista de precios */}
      <ModalConfirmacion
        mostrar={tm.confirmacionCambioLista.mostrar}
        titulo={tm.confirmacionCambioLista.titulo}
        mensaje={tm.confirmacionCambioLista.mensaje}
        textoConfirmar={tm.confirmacionCambioLista.textoConfirmar}
        textoCancelar={tm.confirmacionCambioLista.textoCancelar}
        tipo={tm.confirmacionCambioLista.tipo}
        onConfirmar={() => tm.resolverConfirmacionListaRef.current?.(true)}
        onCancelar={() => tm.resolverConfirmacionListaRef.current?.(false)}
      />
    </div>
  );
}
